import { useState } from 'react';
import dayjs from 'dayjs';
import { DatepickerV2 } from '@client/toolkit';
import { MdDateRange } from 'react-icons/md';
import { colors } from '../../../../styles/color-tokens';

/**
 * Year-only picker styled to match the dark reporting theme.
 * Selecting a year sets the parent range to 1 Jan – 31 Dec of that year.
 *
 * The MUI `slotProps` block lives here (instead of in `DateRangeFilter`) so the
 * caller only sees the public `value` / `onYearChange` API.
 */
export function YearPicker({
  value,
  onYearChange,
}: {
  value: Date | null;
  onYearChange: (startDate: Date, endDate: Date) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <DatepickerV2
      value={value ? dayjs(value) : null}
      views={['year']}
      openTo="year"
      format="YYYY"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={(v) => {
        if (!v) return;
        const year = v.year();
        onYearChange(new Date(year, 0, 1), new Date(year, 11, 31));
      }}
      slotProps={{
        desktopPaper: {
          sx: {
            '& .MuiYearCalendar-root': {
              scrollbarWidth: 'thin',
              scrollbarColor: '#322F2C transparent',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#322F2C',
                borderRadius: '9999px',
              },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#4a4642' },
            },
          },
        },
        field: { readOnly: true },
        textField: {
          size: 'small',
          onClick: () => setOpen(true),
          InputProps: {
            startAdornment: (
              <MdDateRange size={16} className="text-brand-primary hover:cursor-pointer" />
            ),
            endAdornment: <></>,
            onClick: () => setOpen(true),
            className: 'gap-2 cursor-pointer',
          },
          sx: {
            '& .MuiPickersInputBase-root, & .MuiOutlinedInput-root': {
              backgroundColor: colors.background.surface.elevated,
              color: 'white',
              borderRadius: '6px',
              width: 'fit-content',
            },
            '& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiPickersOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline':
              { border: 'none' },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline':
              { border: 'none' },
            '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionAfter, & .MuiPickersInputBase-sectionBefore, & .MuiInputBase-input':
              { color: 'white', fontSize: '14px' },
          },
        },
      }}
    />
  );
}
